import { supabase } from '../lib/supabase/client';
import { QuoteRequest } from '../types/models';
import { Database } from '../types/database.types';

type QuoteRow = Database['public']['Tables']['quote_requests']['Row'];
type QuoteInsert = Database['public']['Tables']['quote_requests']['Insert'];
type QuoteUpdate = Database['public']['Tables']['quote_requests']['Update'];

function toQuoteRequest(row: QuoteRow): QuoteRequest {
  return {
    id: row.id,
    fullName: row.full_name,
    email: row.email,
    phone: row.phone ?? undefined,
    companyName: row.company_name ?? undefined,
    projectType: row.project_type,
    budgetRange: row.budget_range ?? undefined,
    timeline: row.timeline ?? undefined,
    projectDescription: row.project_description,
    status: row.status,
    adminNotes: row.admin_notes ?? undefined,
    estimatedCost: row.estimated_cost ?? undefined,
    quoteSentAt: row.quote_sent_at ?? undefined,
    assignedTo: row.assigned_to ?? undefined,
    submittedAt: row.submitted_at,
    lastUpdated: row.last_updated,
    ipAddress: row.ip_address ?? undefined,
    userAgent: row.user_agent ?? undefined,
    attachments: row.attachments,
  };
}

export const quoteService = {
  async getAll(): Promise<{ data: QuoteRequest[] | null; error: Error | null }> {
    const { data, error } = await supabase
      .from('quote_requests')
      .select('*')
      .order('submitted_at', { ascending: false });

    if (error) return { data: null, error };
    return { data: data?.map(toQuoteRequest) ?? [], error: null };
  },

  async getById(id: string): Promise<{ data: QuoteRequest | null; error: Error | null }> {
    const { data, error } = await supabase
      .from('quote_requests')
      .select('*')
      .eq('id', id)
      .single();

    if (error) return { data: null, error };
    return { data: data ? toQuoteRequest(data) : null, error: null };
  },

  async updateStatus(
    id: string,
    status: QuoteRequest['status']
  ): Promise<{ data: QuoteRequest | null; error: Error | null }> {
    const updateData: QuoteUpdate = { status };
    const { data, error } = await supabase
      .from('quote_requests')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) return { data: null, error };
    return { data: data ? toQuoteRequest(data) : null, error: null };
  },

  async updateNotes(
    id: string,
    adminNotes: string
  ): Promise<{ data: QuoteRequest | null; error: Error | null }> {
    const updateData: QuoteUpdate = { admin_notes: adminNotes };
    const { data, error } = await supabase
      .from('quote_requests')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) return { data: null, error };
    return { data: data ? toQuoteRequest(data) : null, error: null };
  },

  async updateEstimatedCost(
    id: string,
    estimatedCost: number
  ): Promise<{ data: QuoteRequest | null; error: Error | null }> {
    const updateData: QuoteUpdate = { estimated_cost: estimatedCost };
    const { data, error } = await supabase
      .from('quote_requests')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) return { data: null, error };
    return { data: data ? toQuoteRequest(data) : null, error: null };
  },

  async delete(id: string): Promise<{ error: Error | null }> {
    const { error } = await supabase.from('quote_requests').delete().eq('id', id);

    return { error };
  },

  async create(quote: Omit<QuoteRequest, 'id' | 'submittedAt' | 'lastUpdated'>): Promise<{
    data: QuoteRequest | null;
    error: Error | null;
  }> {
    const insertData: QuoteInsert = {
      full_name: quote.fullName,
      email: quote.email,
      phone: quote.phone,
      company_name: quote.companyName,
      project_type: quote.projectType,
      budget_range: quote.budgetRange,
      timeline: quote.timeline,
      project_description: quote.projectDescription,
      status: quote.status ?? 'pending',
      admin_notes: quote.adminNotes,
      estimated_cost: quote.estimatedCost,
      quote_sent_at: quote.quoteSentAt,
      assigned_to: quote.assignedTo,
      ip_address: quote.ipAddress,
      user_agent: quote.userAgent,
      attachments: quote.attachments,
    };

    const { data, error } = await supabase.from('quote_requests').insert(insertData).select().single();

    if (error) return { data: null, error };
    return { data: data ? toQuoteRequest(data) : null, error: null };
  },
};